import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
}

export interface UserAchievement {
  id: string;
  achievement: Achievement;
  earned_at: string;
}

export const useAchievements = (userId?: string) => {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        // Fetch all achievements
        const { data: achievementsData, error: achievementsError } = await supabase
          .from('achievements')
          .select('*')
          .order('created_at');

        if (achievementsError) throw achievementsError;
        setAllAchievements(achievementsData || []);

        // Fetch user's achievements
        if (targetUserId) {
          const { data: userAchievementsData, error: userAchievementsError } = await supabase
            .from('user_achievements')
            .select('id, earned_at, achievement:achievements(*)')
            .eq('user_id', targetUserId)
            .order('earned_at', { ascending: false });

          if (userAchievementsError) throw userAchievementsError;
          setUserAchievements(userAchievementsData || []);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAchievements();
  }, [targetUserId]);

  return {
    userAchievements,
    allAchievements,
    loading,
    error,
  };
};
